import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputField from "../../form/InputField";
import { useUpdateResource } from "../../../hooks/useUpdateResource";
import LoadingButton from "../../form/LoadingButton";
import SubmitButton from "../../form/SubmitButton";
import { useAvatarsByAdmin } from "../../../hooks/admin/avatars.admin";
import LoadingErrorState from "../../ui/LoadingErrorState";

export interface CustomerFormData {
  avatarId: number | null;
  name: string;
  email: string;
}

interface Props {
  defaultValues: CustomerFormData;
  id: number;
  onSubmitSuccess?: () => void;
}

const UpdateCustomerForm: React.FC<Props> = ({ defaultValues, id, onSubmitSuccess }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CustomerFormData>({
    defaultValues,
  });

  const { loading, updateResource } = useUpdateResource();
  const { data: avatars, isLoading, isError } = useAvatarsByAdmin();
  const selectedAvatarId = watch("avatarId");

  const onSubmit: SubmitHandler<CustomerFormData> = async (data) => {
    await updateResource(`customer?customerId=${id}`, data, "admin", {
      method: "PUT",
      successMessage: t("update_success"),
      invalidateQueries: ["customer-detalis-by-admin"],
    });
    if (onSubmitSuccess) onSubmitSuccess();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Avatar Selection */}
      <div>
        <label className="block mb-2 text-sm font-medium text-Black">
          {t("avatar")}
        </label>
        <LoadingErrorState isError={isError} isLoading={isLoading} />
        {avatars && (
          <div className="flex gap-4 flex-wrap">
            {avatars.map((avatar) => (
              <div
                key={avatar.id}
                onClick={() => setValue("avatarId", avatar.id)}
                className={`cursor-pointer rounded-full border-2 p-1 ${
                  selectedAvatarId === avatar.id
                    ? "border-primaryButton border-4"
                    : "border-transparent"
                }`}
              >
                <img
                  src={avatar.url}
                  alt={avatar.id.toString()}
                  className="w-20 h-20 object-contain rounded-full border border-LightGrey3 hover:opacity-80 transition"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Name */}
      <InputField
        label={t("name")}
        placeholder=""
        register={register("name", { required: t("required_field") })}
        error={errors.name}
      />

      {/* Email */}
      <InputField
        label={t("email")}
        placeholder=""
        register={register("email", {
          required: t("required_field"),
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: t("invalid_email"),
          },
        })}
        error={errors.email}
      />

      {/* Buttons */}
      {loading ? <LoadingButton /> : <SubmitButton label={t("update")} />}
    </form>
  );
};

export default UpdateCustomerForm;
